export type TireTuningId = 'touring' | 'sport' | 'all-terrain';
export type SuspensionTuningId = 'comfort' | 'sport' | 'rally';
export type GearingTuningId = 'short' | 'balanced' | 'long';
export type BrakeTuningId = 'road' | 'sport';

export interface VehicleTuning {
  tire: TireTuningId;
  suspension: SuspensionTuningId;
  gearing: GearingTuningId;
  brakes: BrakeTuningId;
}

export type VehicleTuningField = keyof VehicleTuning;

export interface ResolvedVehicleTuning {
  gripMultiplier: number;
  rollingResistanceMultiplier: number;
  accelerationMultiplier: number;
  topSpeedMultiplier: number;
  brakeMultiplier: number;
  /** ABS가 허용하는 종방향 접지 사용률. 스포츠 셋업이 더 높습니다. */
  absLimitRatio: number;
  suspensionStiffness: number;
  suspensionDamping: number;
  suspensionTravel: number;
  bumpResponse: number;
}

export const DEFAULT_VEHICLE_TUNING: Readonly<VehicleTuning> = Object.freeze({
  tire: 'touring',
  suspension: 'comfort',
  gearing: 'balanced',
  brakes: 'road',
});

export const TUNING_CHOICES = {
  tire: [
    { id: 'touring', name: '투어링' },
    { id: 'sport', name: '스포츠' },
    { id: 'all-terrain', name: '올터레인' },
  ],
  suspension: [
    { id: 'comfort', name: '컴포트' },
    { id: 'sport', name: '스포츠' },
    { id: 'rally', name: '랠리' },
  ],
  gearing: [
    { id: 'short', name: '숏' },
    { id: 'balanced', name: '균형' },
    { id: 'long', name: '롱' },
  ],
  brakes: [
    { id: 'road', name: '로드' },
    { id: 'sport', name: '스포츠' },
  ],
} as const;

export function isValidTuningValue(field: VehicleTuningField, value: string): boolean {
  return TUNING_CHOICES[field].some((choice) => choice.id === value);
}

export function resolveVehicleTuning(tuning: VehicleTuning): ResolvedVehicleTuning {
  const tire = {
    touring: { grip: 1, rolling: 1 },
    sport: { grip: 1.12, rolling: 1.06 },
    'all-terrain': { grip: 0.94, rolling: 1.16 },
  }[tuning.tire];
  /*
   * stiffness는 스프링-댐퍼의 ω²입니다(1/s²). 실차 승용 서스펜션의 차체 고유진동수가
   * 대략 1~1.6Hz라서 ω² = (2π·f)² 범위인 40~100을 씁니다. 예전 값(15~29)은 0.6~0.86Hz로
   * 너무 부드러워 요철이 거의 걸러졌습니다.
   * damping은 2ζω 기준이고 ζ는 0.35~0.5입니다. bump는 타이어가 요철을 차체로 전달하는
   * 비율이며, 부드러운 랠리 타이어가 가장 많이 흡수합니다.
   */
  const suspension = {
    comfort: { stiffness: 52, damping: 6.2, travel: 0.12, bump: 0.85 },
    sport: { stiffness: 95, damping: 9.5, travel: 0.075, bump: 1 },
    rally: { stiffness: 40, damping: 5.4, travel: 0.2, bump: 0.7 },
  }[tuning.suspension];
  const gearing = {
    short: { acceleration: 1.1, topSpeed: 0.92 },
    balanced: { acceleration: 1, topSpeed: 1 },
    long: { acceleration: 0.92, topSpeed: 1.08 },
  }[tuning.gearing];
  const sportBrakes = tuning.brakes === 'sport';
  const brakeMultiplier = sportBrakes ? 1.18 : 1;
  const absLimitRatio = sportBrakes ? 0.95 : 0.82;
  return {
    gripMultiplier: tire.grip,
    rollingResistanceMultiplier: tire.rolling,
    accelerationMultiplier: gearing.acceleration,
    topSpeedMultiplier: gearing.topSpeed,
    brakeMultiplier,
    absLimitRatio,
    suspensionStiffness: suspension.stiffness,
    suspensionDamping: suspension.damping,
    suspensionTravel: suspension.travel,
    bumpResponse: suspension.bump,
  };
}
